import React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { useTableContext } from '../../context';
import type { FilterOperator, FilterUIConfig } from '../../types';
import { filterUtils } from '../../hooks/data/use-filters';

export interface QuickFilterPanelProps {
  onClose?: () => void;
}

/**
 * Simple quick-filter panel inspired by MUI DataGrid's Quick Filter.
 * - Single input controlling the table's global searchValue
 * - Parses values by spaces or commas and displays them as chips
 */
export const QuickFilterPanel: React.FC<QuickFilterPanelProps> = ({
  onClose,
}) => {
  const { filterConfigs, filtering, visibleColumns } =
    useTableContext<Record<string, unknown>>();

  // Build effective filter configs from provided configs or table visible columns
  const effectiveFilterConfigs = React.useMemo<FilterUIConfig[]>(() => {
    if (filterConfigs && filterConfigs.length > 0) return filterConfigs;
    return (visibleColumns || []).map((col) => ({
      field: String(col.id),
      label: col.label,
      type: 'text',
    }));
  }, [filterConfigs, visibleColumns]);

  // Staged multi-row filters to match server-side filter UX
  const [draftFilters, setDraftFilters] = React.useState<
    { field: string; operator: FilterOperator | ''; value: unknown }[]
  >([]);

  const getConfigForField = React.useCallback(
    (field: string): FilterUIConfig | undefined =>
      effectiveFilterConfigs.find((c) => c.field === field),
    [effectiveFilterConfigs],
  );

  // console.log('QuickFilterPanel render:', {
  //   draftFilters,
  //   effectiveFilterConfigs,
  //   visibleColumns,
  //   filterConfigs,
  // });

  const getOperatorsForField = React.useCallback(
    (field: string): FilterOperator[] => {
      const cfg = getConfigForField(field);
      if (!cfg) return [] as FilterOperator[];
      return (cfg.operators ||
        filterUtils.getDefaultOperators(cfg.type)) as FilterOperator[];
    },
    [getConfigForField],
  );

  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (initialized) return; // Prevent re-initialization after manual changes

    console.log('Initializing draft filters...');
    // Initialize from current active filters
    const initial = filtering.filters.map((f) => ({
      field: f.field,
      operator: f.operator as FilterOperator,
      value: f.value,
    }));
    if (initial.length > 0) {
      setDraftFilters(initial);
    } else {
      const firstField = effectiveFilterConfigs?.[0]?.field || '';
      if (firstField) {
        const cfg = effectiveFilterConfigs.find((c) => c.field === firstField);
        const ops = cfg
          ? ((cfg.operators ||
              filterUtils.getDefaultOperators(cfg.type)) as FilterOperator[])
          : [];
        setDraftFilters([
          {
            field: firstField,
            operator: (ops[0] || '') as FilterOperator | '',
            value: '',
          },
        ]);
      }
    }
    setInitialized(true);
  }, [filtering.filters, effectiveFilterConfigs, initialized]);

  const addRow = () => {
    const firstField = effectiveFilterConfigs?.[0]?.field || '';
    const ops = firstField
      ? getOperatorsForField(firstField)
      : ([] as FilterOperator[]);
    setDraftFilters((prev) => [
      ...prev,
      {
        field: firstField,
        operator: (ops[0] || '') as FilterOperator | '',
        value: '',
      },
    ]);
  };

  const updateRow = (
    index: number,
    partial: Partial<{
      field: string;
      operator: FilterOperator | '';
      value: unknown;
    }>,
    resetValue = false,
  ) => {
    console.log('updateRow:', {
      index,
      field: partial.field,
      operator: partial.operator,
      value: partial.value,
      resetValue,
    });
    setDraftFilters((prev) => {
      const next = [...prev];
      const current = next[index];
      const updated = { ...current, ...partial } as {
        field: string;
        operator: FilterOperator;
        value: unknown;
      };
      if (resetValue) updated.value = '';
      next[index] = updated;
      console.log('updateRow result:', { current, updated });
      return next;
    });
  };

  const removeRow = (index: number) => {
    setDraftFilters((prev) => prev.filter((_, i) => i !== index));
  };

  const isRowValid = (row: {
    field: string;
    operator: FilterOperator | '';
    value: unknown;
  }) => {
    if (!row.field || !row.operator) return false;
    if (row.operator === 'in' || row.operator === 'notIn') {
      return Array.isArray(row.value) && (row.value as unknown[]).length > 0;
    }
    return row.value !== '' && row.value !== undefined && row.value !== null;
  };

  const canSave =
    draftFilters.length > 0 && draftFilters.every((r) => isRowValid(r));

  const handleSave = () => {
    console.log('💾 SAVING FILTERS:', {
      draftFilters,
      canSave,
      validFilters: draftFilters.filter(isRowValid),
    });

    filtering.clearFilters();
    draftFilters.forEach((f) => {
      console.log('Adding filter:', f);
      filtering.addFilter(f);
    });
    onClose?.();
  };

  const handleCancel = () => onClose?.();

  return (
    <Box sx={{ p: 2, minWidth: 560 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Filters
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {draftFilters.map((row, index) => {
          const cfg = getConfigForField(row.field);
          const operators = getOperatorsForField(row.field);
          return (
            <Box
              key={index}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 1fr 1.4fr 40px',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <FormControl size="small">
                <InputLabel>Column</InputLabel>
                <Select
                  label="Column"
                  value={row.field}
                  onChange={(e) => {
                    const newField = e.target.value;
                    // console.log('Column Select onChange:', newField);

                    // Only update if the field actually changed
                    if (newField !== row.field) {
                      const nextOps = getOperatorsForField(newField);
                      updateRow(
                        index,
                        {
                          field: newField,
                          operator: (nextOps[0] || '') as FilterOperator | '',
                        },
                        true,
                      );
                    }
                  }}
                  MenuProps={{
                    disablePortal: true,
                    disableScrollLock: true,
                  }}
                >
                  {effectiveFilterConfigs?.map((fc) => (
                    <MenuItem key={fc.field} value={fc.field}>
                      {fc.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" disabled={!row.field}>
                <InputLabel>Operator</InputLabel>
                <Select
                  label="Operator"
                  value={row.operator || ''}
                  onChange={(e) => {
                    const newOperator = e.target.value as FilterOperator;
                    // console.log('Operator Select onChange:', newOperator);

                    // Only update if the operator actually changed
                    if (newOperator !== row.operator) {
                      updateRow(index, { operator: newOperator }, true);
                    }
                  }}
                  MenuProps={{
                    disablePortal: true,
                    disableScrollLock: true,
                    PaperProps: { 'data-keepopen': '1' },
                  }}
                >
                  {operators.map((op) => (
                    <MenuItem key={op} value={op}>
                      {filterUtils.getOperatorLabel(op)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {cfg?.type === 'select' ? (
                row.operator === 'in' || row.operator === 'notIn' ? (
                  <Autocomplete
                    multiple
                    size="small"
                    disablePortal
                    options={cfg?.options || []}
                    getOptionLabel={(opt) => opt.label}
                    value={(cfg?.options || []).filter(
                      (opt) =>
                        Array.isArray(row.value) &&
                        (row.value as unknown[]).includes(opt.value),
                    )}
                    onChange={(_, value) =>
                      updateRow(index, { value: value.map((v) => v.value) })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Value" />
                    )}
                    ListboxProps={{
                      // @ts-expect-error custom attribute for click-away guard
                      'data-keepopen': '1',
                    }}
                    disabled={!row.operator}
                  />
                ) : (
                  <FormControl size="small">
                    <InputLabel>Value</InputLabel>
                    <Select
                      label="Value"
                      value={row.value ?? ''}
                      onChange={(e) => {
                        console.log('✅ Value selected:', e.target.value);
                        updateRow(index, {
                          value: e.target.value,
                        });
                      }}
                      MenuProps={{
                        disablePortal: true,
                        disableScrollLock: true,
                      }}
                      disabled={!row.operator}
                    >
                      <MenuItem value="">
                        <em>Select value...</em>
                      </MenuItem>
                      {(cfg?.options || []).map((opt) => (
                        <MenuItem
                          key={String(opt.value)}
                          value={String(opt.value)}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )
              ) : cfg?.type === 'number' ? (
                <TextField
                  size="small"
                  label="Value"
                  type="number"
                  value={(row.value as number | '').toString()}
                  onChange={(e) =>
                    updateRow(index, { value: Number(e.target.value) })
                  }
                  disabled={!row.operator}
                />
              ) : cfg?.type === 'date' ? (
                <TextField
                  size="small"
                  label="Value"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={(row.value as string) || ''}
                  onChange={(e) => updateRow(index, { value: e.target.value })}
                  disabled={!row.operator}
                />
              ) : (
                <TextField
                  size="small"
                  label="Value"
                  value={(row.value as string) || ''}
                  onChange={(e) => updateRow(index, { value: e.target.value })}
                  disabled={!row.operator}
                />
              )}

              <IconButton
                aria-label="remove"
                size="small"
                onClick={() => removeRow(index)}
              >
                <span style={{ fontSize: 14 }}>✕</span>
              </IconButton>
            </Box>
          );
        })}

        <Box>
          <Button variant="outlined" size="sm" onClick={addRow}>
            Add filter
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" size="sm" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={!canSave}
          onClick={handleSave}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};
